import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { DateRangeField } from "@/components/DateRangeField";

const WA_ICON = "M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.78h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.48-8.41Z";
const fmtDate = (d) => d.split("-").reverse().join("/");
const todayIso = () => new Date().toISOString().slice(0, 10);
const waPhone = (raw = "") => {
  const r = raw.trim();
  const digits = r.replace(/[^\d]/g, "").replace(/^00/, "");
  return digits && !r.startsWith("+") && !r.startsWith("00") ? `39${digits}` : digits;
};

export const bookingUrl = (url, f) => {
  if (!url || !f.checkin || !f.checkout || f.checkout <= f.checkin) return url;
  try {
    const u = new URL(url);
    if (u.hostname.endsWith("kross.travel")) {
      u.searchParams.set("from", f.checkin);
      u.searchParams.set("to", f.checkout);
      u.searchParams.set("guests_rooms", `${f.guests},0`);
      return u.toString();
    }
    if (!u.hostname.includes("octorate.com")) return url;
    u.pathname = u.pathname.replace("index.xhtml", "result.xhtml");
    u.searchParams.set("checkin", f.checkin);
    u.searchParams.set("checkout", f.checkout);
    u.searchParams.set("pax", String(f.guests));
    return u.toString();
  } catch {
    return url;
  }
};

function useStay(property) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [f, setF] = useState(() => {
    const ci = params.get("checkin") || "";
    const co = params.get("checkout") || "";
    const g = Math.max(1, parseInt(params.get("guests")) || 2);
    return { checkin: ci >= todayIso() ? ci : "", checkout: co > ci && ci >= todayIso() ? co : "", guests: g };
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const url = property.booking_engine?.url || "";
  const phone = waPhone(property.owner?.phone);
  const book = () => {
    api.post("/public/track", { slug: property.slug, type: "inquiry" }).catch(() => {});
    if (url) window.open(bookingUrl(url, f), "_blank");
  };
  const sendWa = () => {
    if (!f.checkin || !f.checkout || f.checkout <= f.checkin) { toast.error(t("property.waDatesError")); return; }
    const msg = t("property.waMsg").replace("{in}", fmtDate(f.checkin)).replace("{out}", fmtDate(f.checkout)).replace("{n}", f.guests);
    api.post("/public/track", { slug: property.slug, type: "whatsapp" }).catch(() => {});
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return { f, set, url, phone, book, sendWa };
}

function StayFields({ s, max, prefix, compact }) {
  const { t } = useLang();
  const lbl = compact ? "block text-[9px] font-semibold uppercase tracking-wider text-brand-plum/50 mb-0.5" : "block text-[11px] font-semibold uppercase tracking-wider text-brand-plum/50 mb-1";
  const cls = `w-full ${compact ? "h-9 px-1.5 text-xs rounded-lg" : "h-11 px-3 text-sm rounded-xl"} border border-brand-plum/15 bg-white text-brand-plum focus:outline-none focus:border-brand-teal`;
  return (
    <div className={compact ? "grid grid-cols-[1fr_3.2rem] gap-1.5" : "grid grid-cols-2 gap-3"}>
      <div className={compact ? "" : "col-span-2"}><span className={lbl}>{t("home.wDates")}</span>
        <DateRangeField compact={compact} checkin={s.f.checkin} checkout={s.f.checkout} onChange={(ci, co) => { s.set("checkin", ci); s.set("checkout", co); }} testid={`${prefix}-dates`} className={compact ? "h-9 px-2 text-xs rounded-lg" : "h-11 px-3 text-sm rounded-xl"} /></div>
      <label className={compact ? "" : "col-span-2"}><span className={lbl}>{t("home.wGuests")}</span>
        <input type="number" min="1" max={max || 30} value={s.f.guests} onChange={(e) => s.set("guests", Math.max(1, parseInt(e.target.value) || 1))} data-testid={`${prefix}-guests`} className={cls} /></label>
    </div>
  );
}

const WaIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true"><path d={WA_ICON} /></svg>
);
const waBtn = "rounded-xl bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold flex items-center justify-center gap-2 transition-colors";
const bookBtn = "rounded-xl bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold transition-colors disabled:opacity-50";

export function BookingBox({ property }) {
  const { t } = useLang();
  const s = useStay(property);
  return (
    <div data-testid="sticky-booking-box" className="rounded-3xl bg-white p-6 lg:p-7 border border-brand-plum/12 shadow-[0_12px_40px_-10px_rgba(62,33,83,0.15)] lg:sticky lg:top-28">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <span className="block text-xs text-brand-plum/50 leading-none mb-1">A partire da</span>
          <span className="font-serif text-3xl text-brand-plum">€{property.price_per_night}</span>
          <span className="text-brand-plum/50"> {t("common.perNightSuffix")}</span>
        </div>
        {property.discount_percent > 0 && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-tealLight text-brand-tealDark">-{property.discount_percent}% diretto</span>
        )}
      </div>
      <StayFields s={s} max={property.guests} prefix="wa" />
      {s.phone && (
        <button type="button" onClick={s.sendWa} data-testid="wa-request-btn" className={`mt-4 w-full h-12 text-sm px-3 whitespace-nowrap ${waBtn}`}>
          <WaIcon /> {t("property.waRequest")}
        </button>
      )}
      <button type="button" onClick={s.book} disabled={!s.url} data-testid="minisite-book-now-button" className={`mt-3 w-full h-12 ${bookBtn}`}>
        {t("property.checkAvail")}
      </button>
      <p className="text-center text-xs text-brand-plum/40 mt-3 flex items-center justify-center gap-1">
        <ShieldCheck size={12} /> {t("property.directNote")}
      </p>
    </div>
  );
}

export function MobileBookingBar({ property }) {
  const { t } = useLang();
  const s = useStay(property);
  return (
    <div data-testid="mobile-booking-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-brand-plum/10 px-3 pt-2 pb-3 shadow-[0_-8px_24px_-8px_rgba(62,33,83,0.18)]">
      <StayFields s={s} max={property.guests} prefix="mobile" compact />
      <div className="mt-2 flex flex-col gap-1.5">
        {s.phone && (
          <button type="button" onClick={s.sendWa} data-testid="mobile-wa-request-btn" className={`w-full h-10 text-sm whitespace-nowrap ${waBtn}`}>
            <WaIcon size={18} /> {t("property.waShort")}
          </button>
        )}
        <button type="button" onClick={s.book} disabled={!s.url} data-testid="mobile-book-now-button" className={`w-full h-10 text-sm px-2 whitespace-nowrap ${bookBtn}`}>
          {t("property.checkShort")}
        </button>
      </div>
    </div>
  );
}
